import React, { useCallback, useEffect } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ModalDialog, { ModalTransition } from '@atlaskit/modal-dialog';
import { useIntl } from '@atlassian/jira-intl';
import traceUFOPress from '@atlassian/ufo-trace-press';
import { UI_EVENT_TYPE } from '../../common/constants';
import { useCommandPalette } from '../../controllers';
import { MODAL_DIALOG_TEST_ID } from './constants';
import { messages } from './messages';
import { ModalContent } from './modal-content';
import { ModalWrapper } from './modal-wrapper';
import type { CommandPaletteModalProps } from './types';

export const CommandPaletteModal = ({
	defaultPlaceholder,
	defaultFooterContent,
	onAnalytics,
	onErrorAnalytics,
	defaultFallback,
	rootFallback,
}: CommandPaletteModalProps) => {
	const [
		{ commandPaletteSessionId, isOpen, hasPopupOverlay },
		{ close, markAsLoaded, setAnalytics },
	] = useCommandPalette();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	useEffect(() => {
		setAnalytics(onAnalytics, onErrorAnalytics);
	}, [onAnalytics, onErrorAnalytics, setAnalytics]);

	const sendAnalyticsEvent = useCallback(
		(action: string): void => {
			const ev = createAnalyticsEvent({
				action,
				actionSubject: 'commandPalette',
			});
			if (onAnalytics) {
				onAnalytics(ev, UI_EVENT_TYPE, {
					attributes: { commandPaletteSessionId },
				});
			}
		},
		[commandPaletteSessionId, createAnalyticsEvent, onAnalytics],
	);

	const onEscPressed = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape' && isOpen) {
				event.preventDefault();
				event.stopPropagation();
				sendAnalyticsEvent('closed');
				traceUFOPress('command-palette-closed');
				close();
			}
		},
		[isOpen, sendAnalyticsEvent, close],
	);

	useEffect(() => {
		if (hasPopupOverlay) return;
		document.addEventListener('keydown', onEscPressed, true);

		return () => {
			document.removeEventListener('keydown', onEscPressed, true);
		};
	}, [hasPopupOverlay, onEscPressed]);

	useEffect(() => {
		markAsLoaded();
	}, [markAsLoaded]);

	const onClose = useCallback(() => {
		sendAnalyticsEvent('closed');
		traceUFOPress('command-palette-closed');
		close();
	}, [sendAnalyticsEvent, close]);

	const onOpenComplete = useCallback(() => {
		sendAnalyticsEvent('viewed');
	}, [sendAnalyticsEvent]);

	if (!isOpen) return null;

	return (
		<ModalTransition>
			<ModalDialog
				onClose={onClose}
				shouldCloseOnOverlayClick
				shouldCloseOnEscapePress
				autoFocus={false}
				testId={MODAL_DIALOG_TEST_ID}
				onOpenComplete={onOpenComplete}
				width={752}
				isBlanketHidden
				label={formatMessage(messages.modalLabel)}
			>
				<ModalWrapper>
					<ModalContent
						defaultFallback={defaultFallback}
						rootFallback={rootFallback}
						defaultPlaceholder={defaultPlaceholder}
						defaultFooterContent={defaultFooterContent}
						onClose={onClose}
					/>
				</ModalWrapper>
			</ModalDialog>
		</ModalTransition>
	);
};
